import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import icons from "../../../assets/images/constantIcons";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";
import Select from "react-select";
import AddCustomerForm from "../Customer/AddCustomerForm";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import { TimePicker } from "antd";

// Convert time range to 12hr format
const converttime12hrFormat = (timeRange) => {
  if (!timeRange) return "";

  const times = timeRange.split(" - ");
  if (times.length !== 2) return timeRange;

  const convertTo12Hr = (time) => {
    const [hours, minutes] = time.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    const hour = hours % 12 || 12;
    return `${hour}:${minutes.toString().padStart(2, "0")} ${ampm}`;
  };

  return `${convertTo12Hr(times[0])} - ${convertTo12Hr(times[1])}`;
};

// Convert 24-hour format to 12-hour format
const formatTimeTo12 = (time) => {
  if (!time) return "";
  let [hours, minutes] = time.split(":");
  hours = parseInt(hours, 10);
  const modifier = hours >= 12 ? "PM" : "AM";
  const hr12 = hours % 12 || 12;
  return `${hr12.toString().padStart(2, "0")}:${minutes} ${modifier}`;
};

const EditBookingForm = ({ isOpen, onClose, booking_id, fetchPlayers }) => {

  const [customerData, setCustomerData] = useState([]);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [PlayersData, setPlayersData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchCustomers = async () => {
    try {
      const response = await apiService.get("/customer_users?is_active=true");
      setCustomerData(response.data);

    } catch (errors) {
      handleError(errors);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchTabledata = async () => {
    try {
      const response = await apiService.get(`/tables?active=true`);
      setTableData(response.data);
    } catch (error) {
      console.error("Error in fetching data");
    }
  };

  useEffect(() => {
    fetchTabledata();
  }, []);

  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        const response = await apiService.get(`/bookings/${booking_id}`);
        setPlayersData(response.data);

        if (response.data?.table_id) {
          const selected = tableData.find(
            (table) => table.id.toString() === response.data.table_id.toString()
          );
          setSelectedTable(selected || null);
        }
      } catch (errors) {
        handleError(errors);
      }
    };

    if (booking_id) {
      fetchBookingData();
    }
  }, [booking_id, tableData]);

  const playerOptions = customerData?.map((customer) => ({
    label: customer.full_name,
    value: customer.id,
  }));

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      date: PlayersData?.date ? PlayersData?.date.split("T")[0] : "",
      players: PlayersData?.players?.map((player) => player.id) || [],
      start_time: PlayersData?.start_time || "",
      table_id: PlayersData?.table_id || "",
    },
    validationSchema: Yup.object({
      players: Yup.array()
        .min(1, "At least one player must be selected")
        .max(10, "You cannot select more than 10 players")
        .required("Players are required"),
      date: Yup.string()
        .required("Date is required"),
      start_time: Yup.string().required("Start Time is required"),
      table_id: Yup.string().required("Please select a table"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = {
          booking: {
            table_id: values.table_id,
            start_time: values.start_time,
            date: values.date,
            players: values.players,
            booking_type: values.booking_type,
          },
        };

        const response = await apiService.put(
          `/bookings/${booking_id}`,
          payload
        );
        if (response.status === 200) {
          onClose();
          toast.success(response.message, { duration: 2000 });
          fetchPlayers();
        }
      } catch (errors) {
        handleError(errors);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleTableChange = (e) => {
    const tableId = e.target.value;
    formik.setFieldValue("table_id", tableId);
    const tableDetails = tableData.find(
      (table) => table.id.toString() === tableId
    );
    setSelectedTable(tableDetails || null);
    setShowDetails(false);
  };

  return (
    isOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] s:w-[350px] md:w-[460px] relative">
          <div
            className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px]"
            onClick={() => {
              formik.resetForm();
              onClose();
            }}
          >
            <icons.IoClose className="text-green700" />
          </div>
          <h2 className="text-xl font-semibold mb-5 text-G500 text-start">
            Edit Booking
          </h2>

          <form
            onSubmit={formik.handleSubmit}
            className="space-y-4 text-start overflow-y-auto h-[70vh] xl:h-[60vh] 2xl:h-auto vertical"
          >
            {/* Table Dropdown */}
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2">Table <span className="text-red ">*</span></label>
              <select
                name="table_id"
                value={formik.values.table_id}
                onChange={handleTableChange}
                className="inputtext placeholder:text-G50"
              >
                <option value="">Select a table</option>
                {tableData?.map((tabledata) => (
                  <option key={tabledata.id} value={tabledata.id}>
                    {tabledata.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="text-sm hover:text-green hover:underline mt-1"
                onMouseEnter={() => setShowDetails(true)}
                onMouseLeave={() => setShowDetails(false)}
              >
                View table details
              </button>

              {selectedTable && showDetails && (
                <div
                  className="absolute bg-gray-50 p-2 shadow-md border rounded-md w-auto z-30"
                  onMouseEnter={() => setShowDetails(true)}
                  onMouseLeave={() => setShowDetails(false)}
                >
                  <table className="w-full border-collapse border border-gray-300">
                    <tbody>
                      {[
                        "Day Charge",
                        "Night Charge",
                        "Day Time Range",
                        "Night Time Range",
                      ].map((label, index) => (
                        <tr key={index}>
                          <td className="border px-4 py-1 font-medium text-sm">
                            {label}
                          </td>
                          <td className="border px-4 py-1 text-sm">
                            {label.includes("Charge")
                              ? `₹${(
                                label.includes("Day")
                                  ? selectedTable.day_charge
                                  : selectedTable.night_charge
                              )}`
                              : converttime12hrFormat(
                                label.includes("Day")
                                  ? selectedTable.day_time_range
                                  : selectedTable.night_time_range
                              )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {formik.errors.table_id && formik.touched.table_id && (
                <div className="text-red text-sm">{formik.errors.table_id}</div>
              )}
            </div>
            {/* Players */}
            <div className="text-G300">
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium">Players <span className="text-red ">*</span></label>
                <button
                  type="button"
                  onClick={() => setIsCustomerModalOpen(true)}
                  className="text-sm text-green border border-green px-2 py-1 rounded-md"
                >
                  Add Players
                </button>
              </div>
              <Select
                options={playerOptions}
                isMulti
                name="players"
                value={playerOptions?.filter((option) =>
                  formik.values.players.includes(option.value)
                )}
                onChange={(selectedOptions) => {
                  if (selectedOptions.length > 10) {
                    toast.error("You cannot select more than 10 players!", {
                      duration: 2000,
                    });
                  } else {
                    formik.setFieldValue(
                      "players",
                      selectedOptions.map((option) => option.value)
                    );
                  }
                }}
              />
              {formik.errors.players && formik.touched.players && (
                <div className="text-red text-sm">{formik.errors.players}</div>
              )}
            </div>
            {/* Date */}
            <div className="text-G300 w-full">
              <label className="block text-sm font-normal mb-2">Date <span className="text-red ">*</span></label>
              <div className="relative">
                <DatePicker
                  selected={
                    formik.values.date ? new Date(formik.values.date) : null
                  }
                  onChange={(date) =>
                    formik.setFieldValue(
                      "date",
                      date.toISOString().split("T")[0]
                    )
                  }
                 minDate={(() => {
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      yesterday.setHours(0, 0, 0, 0);
                      return yesterday;
                    })()}
                    maxDate={(() => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return today;
                    })()}
                  dateFormat="yyyy-MM-dd"
                  className="inputtext placeholder:text-G50 w-full pr-10"
                />
                <icons.MdOutlineCalendarMonth className="absolute right-3 top-3 text-green pointer-events-none" />
              </div>
              {formik.errors.date && formik.touched.date && (
                <div className="text-red text-sm">{formik.errors.date}</div>
              )}
            </div>
            {/* Start Time Picker */}
            <div className="text-G300 w-full">
              <label className="block text-sm font-normal mb-2">
                Start Time <span className="text-red ">*</span>
              </label>
              <div className="w-full custom-timepicker">
                <TimePicker
                  value={
                    formik.values.start_time
                      ? dayjs(formik.values.start_time, "hh:mm A")
                      : null
                  }
                  format="hh:mm A"
                  use12Hours
                  minuteStep={5}
                  allowClear={false}
                  inputReadOnly={true}
                  showNow={true}
                  className="inputtext w-full focus:outline-none outline-none"
                  onSelect={(time) => {
                    if (time) {
                      formik.setFieldValue("start_time", time.format("hh:mm A"));
                    }
                  }}
                  popupClassName="custom-timepicker-popup"
                  getPopupContainer={() => document.body}
                />
              </div>
              {formik.touched.start_time && formik.errors.start_time && (
                <div className="text-red text-sm">
                  {formik.errors.start_time}
                </div>
              )}
            </div>{" "}
            {/* Submit */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  formik.resetForm();
                  onClose();
                }}
                className="w-[75px] sm:w-[93px] text-sm py-2 text-center border border-green text-green rounded-lg"
              >
                Close
              </button>
              <button
                type="submit"
                className={`w-[75px] sm:w-[93px] text-sm text-center py-2 rounded-lg ${isSubmitting
                  ? "bg-green50 cursor-not-allowed"
                  : "bg-green text-white"
                  }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </div>

        {/* Add Customer Modal */}
        {isCustomerModalOpen && (
          <AddCustomerForm
            isOpen={isCustomerModalOpen}
            onClose={() => setIsCustomerModalOpen(false)}
            fetchCustomers={fetchCustomers}
          />
        )}
      </div>
    )
  );
};

export default EditBookingForm;